import type { ReactNode } from 'react';
import { cn } from '../cn';

export type Tone = 'neutral' | 'primary' | 'success' | 'warning' | 'danger';

const tones: Record<Tone, string> = {
  neutral: 'bg-rail text-ink-2',
  primary: 'bg-primary-soft text-primary-ink',
  success: 'bg-success-soft text-success',
  warning: 'bg-warning-soft text-warning',
  danger: 'bg-danger-soft text-danger',
};

const dots: Record<Tone, string> = {
  neutral: 'bg-ink-4',
  primary: 'bg-primary',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
};

export function Badge({
  tone = 'neutral',
  dot,
  children,
  className,
}: {
  tone?: Tone;
  dot?: boolean;
  children: ReactNode;
  className?: string;
}) {
  return (
    <span
      className={cn(
        'inline-flex h-6 items-center gap-1.5 rounded-sm px-2 text-xs font-medium whitespace-nowrap',
        tones[tone],
        className,
      )}
    >
      {dot && <span className={cn('size-1.5 rounded-full', dots[tone])} aria-hidden />}
      {children}
    </span>
  );
}

/** Small status light with a label, e.g. connection state. */
export function StatusDot({
  tone,
  pulse,
  label,
  className,
}: {
  tone: Tone;
  pulse?: boolean;
  label: ReactNode;
  className?: string;
}) {
  return (
    <span className={cn('inline-flex items-center gap-2 text-sm', className)}>
      <span className="relative flex size-2">
        {pulse && (
          <span
            className={cn('absolute inset-0 animate-ping rounded-full opacity-60', dots[tone])}
          />
        )}
        <span className={cn('relative size-2 rounded-full', dots[tone])} />
      </span>
      {label}
    </span>
  );
}
